/**
 * LiveSupportDashboard.jsx
 *
 * Admin/Operations Analyst dashboard for live chat support:
 * - Live visitor feed with audible notifications
 * - Active chats list
 * - AI conversation history
 * - Take Over functionality
 * - Smart reply suggestions
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import SupportQueue from "../../specialist/SupportQueue";
import { useAuth } from "../../../context/AuthContext";
import { useVisitorTracking } from "../../../hooks/useVisitorTracking";
import {
  useLiveChats,
  useChatConversation,
  takeOverChat,
  sendRepMessage,
  getSuggestedReplies,
  closeChat,
} from "../../../hooks/useLiveChats";
import {
  Users,
  MessageCircle,
  Bot,
  User,
  Send,
  Zap,
  RefreshCw,
  Eye,
  Clock,
  CheckCircle2,
  XCircle,
  Globe,
  Loader2,
  ArrowRight,
  Sparkles,
  Volume2,
  VolumeX,
  Bell,
  BellOff,
  PhoneCall,
  AlertTriangle,
  UserCheck,
  X,
  Radio,
  Headphones,
  MapPin,
} from "lucide-react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../firebaseConfig";

// Notification sound for new visitors (pleasant chime)
const VISITOR_NOTIFICATION_SOUND = "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2telehs+nODKqW0aP5jYu5hpGz+Z2LqZbB4/mte5mm0gP5nYu5pvJT+Y17qZbig/mNe5mW4pP5jXuJltKD+Y17iZbSg/mNe4mW0oP5jXuJltKD+Y17iZbSg/mNe4mW0n";

// Urgent notification sound (longer, more attention-grabbing)
const URGENT_SOUND = "data:audio/wav;base64,UklGRl9nAABXQVZFZm10IBIAAAABAAEAQB8AAIA+AAACABAAZGF0YQBnAACAgICAgICAgICAgICAgICAgICAgICAgICAgICBg4aJi46QkpOVlpeYmJmZmZmZmJeWlZORj42KiIWDgYCAgICAgICBg4aJi46QkpOVlpeYmJmZmZmZmJeWlZORj42KiIWDgYCAgICAgICAgICAgICAgICAgICAgICAgICAgIB/fXp4dXNxb25tbWxsbW1ub3Fyc3V4en1/gICAgICAgICAgICAgICAf317eXZ0cnBubWxramloaGhoaGlqa2xucHJ0dnh7fX+AgICAgICAgICAgIB/fXt5dnRycG5tbGtqaWhoZ2dnZ2doaGlqa2xucHJ0dnh7fX+AgICAgICAgIB/fXt5dnRycG5tbGtqaWhoZ2dnZ2doaGlqa2xucHJ0dnh7fX+AgICAgICAgIB/fXt5dnRycG5tbGtqaWhoZ2dnZ2doaGlqa2xucHJ0dnh7fX+AgICAgICAgIB/fXt5dnRycG5tbGtqaWhoZ2dnZ2doaGlqa2xucHJ0dnh7fX+AgICAgICAgIB/fXt5dnRycG5tbGtqaWhoZ2dnZ2doaGlqa2xucHJ0dnh7fX+AgICAgICAgIB/fXt5dnRycG5tbGtqaWhoZ2dnZ2doaGlqa2xucHJ0dnh7fX+AgICAgICAgA==";

export default function LiveSupportDashboard() {
  const { currentUser, client, refreshClient } = useAuth();
  const { visitors, loading: visitorsLoading, onlineCount } = useVisitorTracking();
  const { chats, closedChats, loading: chatsLoading, activeCount, error: chatsError } = useLiveChats();
  const [chatListView, setChatListView] = useState("open"); // "open" | "closed"
  const listedChats = chatListView === "open" ? chats : closedChats;

  const [activeTab, setActiveTab] = useState("live");
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [selectedVisitor, setSelectedVisitor] = useState(null); // For visitors without active chat
  const [messageInput, setMessageInput] = useState("");
  const [sending, setSending] = useState(false);
  const [suggestedReplies, setSuggestedReplies] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Visitor notification state
  const [visitorSoundEnabled, setVisitorSoundEnabled] = useState(() => {
    const saved = localStorage.getItem("live_support_visitor_sound");
    return saved !== "false";
  });
  const [visitorNotificationsEnabled, setVisitorNotificationsEnabled] = useState(false);

  // Alias setup state
  const [aliasBannerInput, setAliasBannerInput] = useState("");
  const [aliasBannerSaving, setAliasBannerSaving] = useState(false);

  const messagesEndRef = useRef(null);
  const visitorAudioRef = useRef(null);
  const urgentAudioRef = useRef(null);
  const urgentIntervalRef = useRef(null);
  const prevUrgentCountRef = useRef(0);
  const notifiedVisitorsRef = useRef(new Set()); // Track ALL visitors we've notified about this session
  const initialLoadRef = useRef(true);

  // Count chats that need human attention
  const urgentChats = chats.filter((c) => c.needsHumanAttention && !c.humanTakeover);
  const urgentCount = urgentChats.length;

  // Filter out staff members from visitor list
  const filteredVisitors = useMemo(() => {
    return visitors.filter((visitor) => {
      // Exclude current logged-in user
      if (visitor.userId && currentUser?.uid && visitor.userId === currentUser.uid) {
        return false;
      }
      // Exclude visitors on admin or specialist pages
      const page = visitor.page?.toLowerCase() || "";
      if (page.startsWith("/admin") || page.startsWith("/specialist") || page.startsWith("/mentor")) {
        return false;
      }
      // Exclude signed-in employees by role
      const role = visitor.userRole?.toLowerCase();
      if (role === "admin" || role === "specialist" || role === "mentor") {
        return false;
      }
      return true;
    });
  }, [visitors, currentUser?.uid]);

  const filteredVisitorCount = filteredVisitors.length;

  // Initialize visitor notification audio
  useEffect(() => {
    visitorAudioRef.current = new Audio(VISITOR_NOTIFICATION_SOUND);
    visitorAudioRef.current.volume = 0.6;
    urgentAudioRef.current = new Audio(URGENT_SOUND);
    urgentAudioRef.current.volume = 0.7;
  }, []);

  // Play urgent sound repeatedly when there are urgent chats
  useEffect(() => {
    // Clear any existing interval
    if (urgentIntervalRef.current) {
      clearInterval(urgentIntervalRef.current);
      urgentIntervalRef.current = null;
    }

    if (urgentCount > 0 && visitorSoundEnabled) {
      // Play immediately when first urgent chat arrives
      if (prevUrgentCountRef.current === 0 || urgentCount > prevUrgentCountRef.current) {
        if (urgentAudioRef.current) {
          urgentAudioRef.current.play().catch(() => {});
        }

        // Show browser notification
        if (visitorNotificationsEnabled) {
          new Notification("🚨 URGENT: Human Requested!", {
            body: `${urgentCount} visitor${urgentCount > 1 ? "s" : ""} waiting for a live representative!`,
            icon: "/favicon.ico",
            tag: "urgent-support",
            requireInteraction: true,
          });
        }
      }

      // Repeat sound every 15 seconds while urgent chats exist
      urgentIntervalRef.current = setInterval(() => {
        if (urgentAudioRef.current && visitorSoundEnabled) {
          urgentAudioRef.current.play().catch(() => {});
        }
      }, 15000);
    }

    prevUrgentCountRef.current = urgentCount;

    return () => {
      if (urgentIntervalRef.current) {
        clearInterval(urgentIntervalRef.current);
      }
    };
  }, [urgentCount, visitorSoundEnabled, visitorNotificationsEnabled]);

  // Request browser notification permission
  useEffect(() => {
    if ("Notification" in window) {
      if (Notification.permission === "granted") {
        setVisitorNotificationsEnabled(true);
      } else if (Notification.permission !== "denied") {
        Notification.requestPermission().then((permission) => {
          setVisitorNotificationsEnabled(permission === "granted");
        });
      }
    }
  }, []);

  // Save visitor sound preference
  useEffect(() => {
    localStorage.setItem("live_support_visitor_sound", String(visitorSoundEnabled));
  }, [visitorSoundEnabled]);

  // Detect new visitors and play notification ONCE per visitor per admin session
  useEffect(() => {
    if (visitorsLoading) return;

    // Skip notification on initial load, but still track current visitors
    if (initialLoadRef.current) {
      filteredVisitors.forEach((v) => notifiedVisitorsRef.current.add(v.id));
      initialLoadRef.current = false;
      return;
    }

    // Find truly new visitors (never notified about in this admin session)
    // Only consider filtered visitors (excludes staff)
    const newVisitors = filteredVisitors.filter((v) => !notifiedVisitorsRef.current.has(v.id));

    if (newVisitors.length > 0) {
      // Mark these visitors as notified (they won't trigger sound again even if they leave and return)
      newVisitors.forEach((v) => notifiedVisitorsRef.current.add(v.id));

      // Play sound if enabled - only once per new visitor
      if (visitorSoundEnabled && visitorAudioRef.current) {
        visitorAudioRef.current.play().catch(() => {});
      }

      // Show browser notification if enabled and tab is hidden
      if (visitorNotificationsEnabled && document.hidden) {
        const visitorNames = newVisitors.map((v) => v.userName || "Anonymous").join(", ");
        new Notification("New Visitor Online", {
          body: `${newVisitors.length === 1 ? visitorNames + " is" : newVisitors.length + " visitors are"} now on the site`,
          icon: "/favicon.ico",
          tag: "visitor-online",
        });
      }
    }
  }, [filteredVisitors, visitorsLoading, visitorSoundEnabled, visitorNotificationsEnabled]);

  // Format session duration
  const formatSessionDuration = (sessionStart) => {
    if (!sessionStart) return "";
    const now = Date.now();
    const durationMs = now - sessionStart;
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Format a dwell duration (milliseconds spent on a single page)
  const formatDwell = (ms) => {
    if (!ms || ms < 0) return "0s";
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  // Update session durations every second
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Get selected chat details
  const { chat: selectedChat, messages, loading: chatLoading } = useChatConversation(selectedChatId);

  // Get rep display name from user profile — prefer chatAlias for safety
  // ONLY use chatAlias — never fall back to real name for customer-facing messages
  const repDisplayName = client?.chatAlias || null;
  const hasAlias = !!repDisplayName;

  // Note: Auto-scroll removed to prevent page jumping

  // Load suggested replies when chat is taken over
  useEffect(() => {
    if (selectedChat?.humanTakeover && messages.length > 0) {
      loadSuggestions();
    }
  }, [selectedChat?.humanTakeover, messages.length]);

  const loadSuggestions = async () => {
    if (!messages.length) return;
    setLoadingSuggestions(true);
    const suggestions = await getSuggestedReplies(messages);
    setSuggestedReplies(suggestions);
    setLoadingSuggestions(false);
  };

  const handleTakeOver = async () => {
    if (!selectedChatId) return;
    await takeOverChat(selectedChatId, currentUser.uid, repDisplayName);
    loadSuggestions();
  };

  const handleSendMessage = async (text = messageInput) => {
    if (!text.trim() || !selectedChatId || sending) return;
    setSending(true);
    setMessageInput("");
    await sendRepMessage(selectedChatId, text.trim(), currentUser.uid, repDisplayName);
    setSending(false);
    loadSuggestions();
  };

  const handleCloseChat = async () => {
    if (!selectedChatId) return;
    await closeChat(selectedChatId);
    setSelectedChatId(null);
    setSelectedVisitor(null);
  };

  // Handle clicking on a visitor - show their info and chat if available
  const handleSelectVisitor = (visitor) => {
    // Find if this visitor has an active chat
    const visitorChat = chats.find((chat) => chat.visitorId === visitor.visitorId);

    // Always set the visitor for the header display
    setSelectedVisitor(visitor);

    // Set the chat ID if they have one (to load messages)
    if (visitorChat) {
      setSelectedChatId(visitorChat.id);
    } else {
      setSelectedChatId(null);
    }
  };

  // Get the live visitor data (updates in real-time as they navigate)
  const liveSelectedVisitor = selectedVisitor
    ? visitors.find((v) => v.id === selectedVisitor.id) || selectedVisitor
    : null;

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    if (isToday) return `Today, ${time}`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + `, ${time}`;
  };

  const getPageName = (path) => {
    if (!path) return "Unknown";
    const segments = path.split("/").filter(Boolean);
    if (segments.length === 0) return "Home";
    return segments[segments.length - 1].replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Mandatory alias gate — block the entire page until alias is set
  if (!hasAlias) {
    return (
      <div className="max-w-lg mx-auto mt-20">
        <div className="rounded-2xl border border-blue-500/30 bg-blue-500/5 p-8 text-center space-y-6">
          <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto">
            <UserCheck size={32} className="text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-black text-white">Set Your Chat Alias</h2>
            <p className="text-sm text-slate-400 mt-2">
              Before you can go online and assist customers, you must set a chat alias.
              This protects your privacy — customers will only see your alias, never your real name.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              We recommend using your first name and last initial (e.g. Sarah W.)
            </p>
          </div>
          <div className="flex items-center justify-center gap-3">
            <input
              type="text"
              value={aliasBannerInput}
              onChange={(e) => setAliasBannerInput(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Enter" && aliasBannerInput.trim()) {
                  setAliasBannerSaving(true);
                  try {
                    await updateDoc(doc(db, "users", currentUser.uid), { chatAlias: aliasBannerInput.trim() });
                    if (refreshClient) await refreshClient();
                  } finally {
                    setAliasBannerSaving(false);
                  }
                }
              }}
              placeholder="e.g. Sarah W."
              className="px-4 py-3 rounded-xl border border-blue-500/30 bg-slate-800 text-white text-sm outline-none focus:border-blue-500 w-48"
            />
            <button
              onClick={async () => {
                if (!aliasBannerInput.trim()) return;
                setAliasBannerSaving(true);
                try {
                  await updateDoc(doc(db, "users", currentUser.uid), { chatAlias: aliasBannerInput.trim() });
                  if (refreshClient) await refreshClient();
                } finally {
                  setAliasBannerSaving(false);
                }
              }}
              disabled={!aliasBannerInput.trim() || aliasBannerSaving}
              className="px-6 py-3 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {aliasBannerSaving ? "Saving..." : "Save & Go Online"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-20">

      {/* URGENT ALERT BANNER — always visible across all tabs */}
      {urgentCount > 0 && (
        <div className="animate-pulse bg-gradient-to-r from-red-600 via-orange-500 to-red-600 rounded-2xl p-4 shadow-lg shadow-red-500/30 border-2 border-red-400">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <PhoneCall size={32} className="text-white animate-bounce" />
                <span className="absolute -top-1 -right-1 flex h-4 w-4">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-4 w-4 bg-white text-red-600 text-[10px] font-black items-center justify-center">
                    {urgentCount}
                  </span>
                </span>
              </div>
              <div>
                <h2 className="text-white font-black text-lg flex items-center gap-2">
                  <AlertTriangle className="animate-pulse" size={20} />
                  HUMAN REQUESTED!
                </h2>
                <p className="text-white/90 text-sm">
                  {urgentCount} visitor{urgentCount > 1 ? "s are" : " is"} waiting for a live representative
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setActiveTab("live");
                const firstUrgent = urgentChats[0];
                if (firstUrgent) {
                  setSelectedChatId(firstUrgent.id);
                  setSelectedVisitor(null);
                }
              }}
              className="px-6 py-3 bg-white text-red-600 rounded-xl font-black hover:bg-red-50 transition-colors flex items-center gap-2 shadow-lg"
            >
              <UserCheck size={18} />
              Take Over Now
            </button>
          </div>
        </div>
      )}

      {/* Tab Bar */}
      <div className="flex items-center gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 w-fit">
        <button
          onClick={() => setActiveTab("live")}
          className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            activeTab === "live"
              ? "bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 shadow-sm"
              : urgentCount > 0
                ? "text-red-500 dark:text-red-400 bg-red-500/10 animate-pulse"
                : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          <Radio size={14} />
          Live Monitor
          {urgentCount > 0 && activeTab !== "live" && (
            <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 text-white text-[10px] font-black items-center justify-center">
                {urgentCount}
              </span>
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("tickets")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            activeTab === "tickets"
              ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
              : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          <Headphones size={14} />
          Ticket Queue
        </button>
      </div>

      {/* Live Monitor Tab */}
      {activeTab === "live" && (
      <>
      {/* Stats Bar */}
      <div className="flex items-center justify-end gap-3">
        <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 text-green-500 text-xs font-bold">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          {filteredVisitorCount} Online
        </span>
        <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-500 text-xs font-bold">
          <MessageCircle size={12} />
          {activeCount} Active Chats
        </span>

        {/* Visitor Sound Toggle */}
        <button
          onClick={() => setVisitorSoundEnabled(!visitorSoundEnabled)}
          className={`p-2 rounded-lg transition-colors ${
            visitorSoundEnabled
              ? "bg-emerald-500/10 text-emerald-500"
              : "bg-gray-100 dark:bg-gray-800 text-gray-400"
          }`}
          title={visitorSoundEnabled ? "Visitor sound on" : "Visitor sound off"}
        >
          {visitorSoundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </button>

        {/* Browser Notification Toggle */}
        <button
          onClick={() => {
            if (!visitorNotificationsEnabled && "Notification" in window) {
              Notification.requestPermission().then((p) => {
                setVisitorNotificationsEnabled(p === "granted");
              });
            } else {
              setVisitorNotificationsEnabled(!visitorNotificationsEnabled);
            }
          }}
          className={`p-2 rounded-lg transition-colors ${
            visitorNotificationsEnabled
              ? "bg-emerald-500/10 text-emerald-500"
              : "bg-gray-100 dark:bg-gray-800 text-gray-400"
          }`}
          title={visitorNotificationsEnabled ? "Browser notifications on" : "Browser notifications off"}
        >
          {visitorNotificationsEnabled ? <Bell size={16} /> : <BellOff size={16} />}
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {urgentCount > 0 && (
          <div className="p-5 rounded-2xl border-2 border-red-500 bg-red-500/10 animate-pulse">
            <div className="flex items-center gap-2 mb-3">
              <PhoneCall size={16} className="text-red-500 animate-bounce" />
              <p className="text-[10px] font-black uppercase tracking-widest text-red-500">URGENT</p>
            </div>
            <p className="text-3xl font-black text-red-600">{urgentCount}</p>
          </div>
        )}
        <div className="p-5 rounded-2xl border border-gray-300 dark:border-gray-700 bg-transparent">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} className="text-emerald-500" />
            <p className="text-[10px] font-black uppercase tracking-widest opacity-40">Live Visitors</p>
          </div>
          <p className="text-3xl font-black">{filteredVisitorCount}</p>
        </div>
        <div className="p-5 rounded-2xl border border-gray-300 dark:border-gray-700 bg-transparent">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle size={16} className="text-blue-500" />
            <p className="text-[10px] font-black uppercase tracking-widest opacity-40">Active Chats</p>
          </div>
          <p className="text-3xl font-black">{activeCount}</p>
        </div>
        <div className="p-5 rounded-2xl border border-gray-300 dark:border-gray-700 bg-transparent">
          <div className="flex items-center gap-2 mb-3">
            <Bot size={16} className="text-indigo-500" />
            <p className="text-[10px] font-black uppercase tracking-widest opacity-40">AI Handling</p>
          </div>
          <p className="text-3xl font-black">{chats.filter((c) => !c.humanTakeover).length}</p>
        </div>
        <div className="p-5 rounded-2xl border border-gray-300 dark:border-gray-700 bg-transparent">
          <div className="flex items-center gap-2 mb-3">
            <User size={16} className="text-yellow-500" />
            <p className="text-[10px] font-black uppercase tracking-widest opacity-40">Human Assisted</p>
          </div>
          <p className="text-3xl font-black">{chats.filter((c) => c.humanTakeover).length}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Visitors & Chats List */}
        <div className="space-y-4" style={{ height: 'calc(100vh - 240px)', minHeight: '500px', maxHeight: '800px', display: 'flex', flexDirection: 'column' }}>
          {/* Live Visitors */}
          <div className="rounded-2xl border border-gray-300 dark:border-gray-700 overflow-hidden flex-shrink-0" style={{ maxHeight: '40%' }}>
            <div className="px-4 py-3 border-b border-gray-300 dark:border-gray-700 bg-emerald-500/10 flex items-center gap-2">
              <Eye size={16} className="text-emerald-500" />
              <h3 className="text-xs font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                Live Visitors
              </h3>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100% - 44px)' }}>
              {visitorsLoading ? (
                <div className="p-8 text-center">
                  <Loader2 size={24} className="animate-spin mx-auto opacity-30" />
                </div>
              ) : filteredVisitors.length === 0 ? (
                <div className="p-8 text-center opacity-40">
                  <Globe size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-xs">No visitors online</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredVisitors.map((visitor) => {
                    const hasActiveChat = chats.some((c) => c.visitorId === visitor.visitorId);
                    const isSelected = selectedVisitor?.id === visitor.id ||
                      (selectedChatId && chats.find((c) => c.id === selectedChatId)?.visitorId === visitor.visitorId);

                    return (
                      <button
                        key={visitor.id}
                        onClick={() => handleSelectVisitor(visitor)}
                        className={`w-full text-left p-3 transition-colors ${
                          isSelected
                            ? "bg-emerald-50 dark:bg-emerald-900/20 border-l-2 border-emerald-500"
                            : "hover:bg-gray-50 dark:hover:bg-gray-800/50 border-l-2 border-transparent"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-sm truncate">
                            {visitor.userName || "Anonymous"}
                          </span>
                          <div className="flex items-center gap-2">
                            {hasActiveChat && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-500 font-bold">
                                CHAT
                              </span>
                            )}
                            {visitor.sessionStart && (
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-500">
                                <Clock size={8} className="inline mr-1" />
                                {formatSessionDuration(visitor.sessionStart)}
                              </span>
                            )}
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs opacity-50 mb-1">
                          <Globe size={10} />
                          <span className="truncate font-medium">{getPageName(visitor.page)}</span>
                        </div>
                        <div className="text-[10px] opacity-40 font-mono truncate pl-4">
                          {visitor.page || "/"}
                        </div>
                        {visitor.userEmail && (
                          <div className="text-[10px] opacity-40 truncate pl-4 mt-0.5">
                            {visitor.userEmail}
                          </div>
                        )}
                        {(visitor.city || visitor.country || visitor.ip) && (
                          <div className="flex items-center gap-1 text-[10px] opacity-40 truncate pl-4 mt-0.5">
                            <MapPin size={10} className="shrink-0" />
                            <span className="truncate">
                              {[visitor.city, visitor.country].filter(Boolean).join(", ") || visitor.ip}
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Active Chats */}
          <div className="rounded-2xl border border-gray-300 dark:border-gray-700 overflow-hidden flex-1 flex flex-col min-h-0">
            <div className="px-4 py-3 border-b border-gray-300 dark:border-gray-700 bg-blue-500/10 flex items-center gap-2 flex-shrink-0">
              <MessageCircle size={16} className="text-blue-500" />
              <h3 className="text-xs font-black uppercase tracking-widest text-blue-600 dark:text-blue-400">
                Chats
              </h3>
              <div className="ml-auto flex gap-1">
                {[
                  ["open", `Open (${chats.length})`],
                  ["closed", `Closed (${closedChats.length})`],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setChatListView(key)}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      chatListView === key
                        ? "bg-blue-600 text-white"
                        : "text-blue-600 dark:text-blue-400 hover:bg-blue-500/10"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {chatsError && (
              <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-300 dark:border-red-800 flex items-start gap-2 text-xs text-red-700 dark:text-red-300">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  Could not load chats from Havlo: {chatsError}. Messages may be waiting. Check the Havlo dashboard directly.
                </span>
              </div>
            )}
            <div className="overflow-y-auto flex-1">
              {chatsLoading ? (
                <div className="p-8 text-center">
                  <Loader2 size={24} className="animate-spin mx-auto opacity-30" />
                </div>
              ) : listedChats.length === 0 ? (
                <div className="p-8 text-center opacity-40">
                  <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-xs">{chatListView === "open" ? "No open chats" : "No closed chats"}</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {listedChats.map((chat) => {
                    const isUrgent = chat.needsHumanAttention && !chat.humanTakeover;
                    return (
                      <button
                        key={chat.id}
                        onClick={() => {
                          setSelectedChatId(chat.id);
                          setSelectedVisitor(null);
                        }}
                        className={`w-full p-3 text-left transition-colors relative ${
                          isUrgent
                            ? "bg-red-50 dark:bg-red-900/20 animate-pulse ring-2 ring-red-500 ring-inset"
                            : selectedChatId === chat.id
                            ? "bg-blue-50 dark:bg-blue-900/20"
                            : "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        }`}
                      >
                        {isUrgent && (
                          <div className="absolute -top-1 -right-1 flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white text-[8px] font-black rounded-full animate-bounce">
                            <PhoneCall size={8} />
                            URGENT
                          </div>
                        )}
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-bold text-sm truncate">
                            {chat.visitorName || "Visitor"}
                          </span>
                          <div className="flex items-center gap-1">
                            {isUrgent ? (
                              <span className="px-2 py-0.5 rounded-full bg-red-600 text-white text-[8px] font-bold uppercase animate-pulse">
                                Needs Rep
                              </span>
                            ) : chat.humanTakeover ? (
                              <span className="px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-600 text-[8px] font-bold uppercase">
                                Human
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-600 text-[8px] font-bold uppercase">
                                AI
                              </span>
                            )}
                            {chat.origin === "outbound" && (
                              <span className="px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-600 text-[8px] font-bold uppercase">
                                Outbound
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-xs opacity-50 truncate">
                          {chat.lastMessageText || "No messages"}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-[10px] opacity-40">
                          <Clock size={10} />
                          {formatTime(chat.lastMessageIso)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: Chat Window */}
        <div className="lg:col-span-2 rounded-2xl border border-gray-300 dark:border-gray-700 overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 240px)', minHeight: '500px', maxHeight: '800px' }}>
          {/* No selection state */}
          {!liveSelectedVisitor && !selectedChatId ? (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <MessageCircle size={48} className="mx-auto mb-4 opacity-20" />
                <p className="text-sm opacity-40">Select a visitor or chat to view details</p>
              </div>
            </div>
          ) : chatLoading && selectedChatId ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 size={32} className="animate-spin opacity-30" />
            </div>
          ) : (
            <>
              {/* Condensed Visitor Header */}
              <div className="px-4 py-2 border-b border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center justify-between">
                  {/* Left: Visitor Info */}
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <User size={16} className="text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-sm">
                          {liveSelectedVisitor?.userName || selectedChat?.visitorName || "Visitor"}
                        </h3>
                        {selectedChatId && (
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                            selectedChat?.humanTakeover
                              ? "bg-yellow-500/10 text-yellow-600"
                              : "bg-indigo-500/10 text-indigo-600"
                          }`}>
                            {selectedChat?.humanTakeover ? "HUMAN" : "AI BOT"}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] opacity-50">
                        {liveSelectedVisitor?.userEmail || selectedChat?.userEmail || "Guest"}
                      </p>
                    </div>
                  </div>

                  {/* Center: Live Page & Session */}
                  <div className="flex-1 mx-4 flex items-center justify-center gap-4">
                    <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-emerald-500/10">
                      <Globe size={12} className="text-emerald-500" />
                      <div className="text-xs">
                        <span className="font-bold text-emerald-600 dark:text-emerald-400">
                          {getPageName(liveSelectedVisitor?.page || selectedChat?.currentPage)}
                        </span>
                        <span className="ml-2 font-mono opacity-40 text-[10px]">
                          {liveSelectedVisitor?.page || selectedChat?.currentPage || "/"}
                        </span>
                      </div>
                    </div>
                    {liveSelectedVisitor?.sessionStart && (
                      <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-blue-500/10 text-blue-500 text-xs font-mono">
                        <Clock size={10} />
                        {formatSessionDuration(liveSelectedVisitor.sessionStart)}
                      </div>
                    )}
                    {(liveSelectedVisitor?.city || liveSelectedVisitor?.country || liveSelectedVisitor?.ip) && (
                      <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400 text-xs">
                        <MapPin size={10} />
                        <span className="font-medium">
                          {[liveSelectedVisitor?.city, liveSelectedVisitor?.region, liveSelectedVisitor?.country]
                            .filter(Boolean)
                            .join(", ") || "Unknown"}
                        </span>
                        {liveSelectedVisitor?.ip && (
                          <span className="ml-1 font-mono opacity-50 text-[10px]">{liveSelectedVisitor.ip}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center gap-2">
                    {selectedChatId && !selectedChat?.humanTakeover && (
                      <button
                        onClick={handleTakeOver}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-500 text-black text-[10px] font-bold hover:bg-yellow-400 transition-colors"
                      >
                        <Zap size={12} /> Take Over
                      </button>
                    )}
                    {selectedChatId && (
                      <button
                        onClick={handleCloseChat}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 text-[10px] font-bold hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                      >
                        <XCircle size={12} />
                        End Chat
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setSelectedVisitor(null);
                        setSelectedChatId(null);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors opacity-60 hover:opacity-100 text-[10px] font-bold"
                    >
                      <X size={12} />
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>

              {/* Page Journey — per-page dwell time this session */}
              {liveSelectedVisitor?.pageVisits?.length > 0 && (
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-slate-800/20">
                  <p className="text-[9px] font-black uppercase tracking-widest opacity-40 mb-1.5 flex items-center gap-1">
                    <Eye size={10} /> Page Journey
                  </p>
                  <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
                    {liveSelectedVisitor.pageVisits.map((visit, i, arr) => {
                      const isLast = i === arr.length - 1;
                      const end = isLast
                        ? (liveSelectedVisitor.lastSeen || Date.now())
                        : arr[i + 1].enteredAt;
                      const dwell = formatDwell(end - visit.enteredAt);
                      return (
                        <div key={`${visit.path}-${visit.enteredAt}`} className="flex items-center gap-1.5 shrink-0">
                          {i > 0 && <ArrowRight size={10} className="opacity-30 shrink-0" />}
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] ${
                            isLast
                              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold"
                              : "bg-gray-200/60 dark:bg-slate-700/60 opacity-70"
                          }`}>
                            <span className="font-medium max-w-[120px] truncate">{getPageName(visit.path)}</span>
                            <span className="font-mono opacity-60">{dwell}{isLast ? " · now" : ""}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-slate-800/30">
                {/* Flashing Rep Requested Banner inside chat */}
                {selectedChat?.needsHumanAttention && !selectedChat?.humanTakeover && (
                  <div className="flex justify-center my-2">
                    <div className="px-5 py-2.5 rounded-xl bg-red-500/10 border-2 border-red-500/40 text-center animate-pulse shadow-lg shadow-red-500/10">
                      <div className="flex items-center gap-2 text-red-500">
                        <PhoneCall size={14} className="animate-bounce" />
                        <span className="text-xs font-black uppercase tracking-widest">Visitor Requested a Live Rep</span>
                        <PhoneCall size={14} className="animate-bounce" />
                      </div>
                    </div>
                  </div>
                )}
                {messages && messages.length > 0 ? (
                  messages.map((msg) => {
                    const isStaff = msg.sender === "rep" || msg.sender === "ai";
                    const isSystem = msg.sender === "system";
                    return (
                    <div
                      key={msg.id}
                      className={`flex ${isSystem ? "justify-center" : isStaff ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[75%] px-4 py-2.5 rounded-2xl ${
                          msg.sender === "visitor"
                            ? "bg-white dark:bg-slate-700 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-600 rounded-bl-sm"
                            : msg.sender === "ai"
                            ? "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-900 dark:text-indigo-100 border border-indigo-200 dark:border-indigo-700 rounded-br-sm"
                            : msg.sender === "system"
                            ? "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs italic rounded-lg mx-auto text-center"
                            : "bg-blue-600 text-white rounded-br-sm"
                        }`}
                      >
                        {msg.sender === "visitor" && (
                          <p className="text-[10px] font-bold mb-1 opacity-60 flex items-center gap-1">
                            <User size={10} />
                            {msg.senderName || selectedChat?.visitorName || "Visitor"}
                          </p>
                        )}
                        {msg.sender === "ai" && (
                          <p className="text-[10px] font-bold mb-1 opacity-60 flex items-center gap-1">
                            <Bot size={10} />
                            AI Bot
                          </p>
                        )}
                        {msg.sender === "rep" && (
                          <p className="text-[10px] font-bold mb-1 opacity-80 flex items-center gap-1 text-blue-100">
                            <User size={10} />
                            {msg.senderName || "Support Rep"}
                          </p>
                        )}
                        <p className="text-sm leading-relaxed">{msg.text}</p>
                        <p className={`text-[9px] mt-1 ${msg.sender === "rep" ? "opacity-60 text-blue-200" : "opacity-40"}`}>{formatTime(msg.timestamp)}</p>
                      </div>
                    </div>
                    );
                  })
                ) : (
                  <div className="flex-1 flex items-center justify-center text-center py-12">
                    <div>
                      <MessageCircle size={32} className="mx-auto mb-3 opacity-20" />
                      <p className="text-sm opacity-40">No chat messages yet</p>
                      <p className="text-xs opacity-30 mt-1">Visitor is browsing without starting a chat</p>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Smart Replies (only after takeover) */}
              {selectedChat?.humanTakeover && (
                <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-indigo-50 dark:bg-indigo-900/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={12} className="text-indigo-500" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 dark:text-indigo-400">
                      AI Suggested Replies
                    </span>
                    <button
                      onClick={loadSuggestions}
                      disabled={loadingSuggestions}
                      className="ml-auto p-1 hover:bg-indigo-100 dark:hover:bg-indigo-800 rounded transition-colors"
                    >
                      <RefreshCw size={12} className={`text-indigo-500 ${loadingSuggestions ? "animate-spin" : ""}`} />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {loadingSuggestions ? (
                      <span className="text-xs opacity-50">Loading suggestions...</span>
                    ) : (
                      suggestedReplies.map((reply, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSendMessage(reply)}
                          className="px-3 py-1.5 text-xs bg-white dark:bg-slate-700 border border-indigo-200 dark:border-indigo-600 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-800 transition-colors text-left"
                        >
                          {reply}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Input (only after takeover) */}
              {selectedChat?.humanTakeover && (
                <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Type your message..."
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-transparent text-sm outline-none focus:border-blue-500"
                    />
                    <button
                      onClick={() => handleSendMessage()}
                      disabled={!messageInput.trim() || sending}
                      className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    </button>
                  </div>
                </div>
              )}

              {/* AI Handling / Rep Requested Notice */}
              {selectedChatId && !selectedChat?.humanTakeover && (
                selectedChat?.needsHumanAttention ? (
                  <div className="p-3 border-t-2 border-red-500 bg-red-50 dark:bg-red-900/30 text-center animate-pulse">
                    <div className="flex items-center justify-center gap-2 text-red-600 dark:text-red-400">
                      <PhoneCall size={14} className="animate-bounce" />
                      <span className="text-xs font-black uppercase tracking-wider">Live Rep Requested</span>
                    </div>
                    <p className="text-[10px] text-red-500/70 mt-0.5">
                      Click "Take Over" to respond now
                    </p>
                  </div>
                ) : (
                  <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-indigo-50 dark:bg-indigo-900/20 text-center">
                    <div className="flex items-center justify-center gap-2 text-indigo-600 dark:text-indigo-400">
                      <Bot size={14} />
                      <span className="text-xs font-medium">AI Bot is handling this conversation</span>
                    </div>
                    <p className="text-[10px] opacity-50 mt-0.5">
                      Click "Take Over" to respond directly
                    </p>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>
      </>
      )}

      {/* Ticket Queue Tab */}
      {activeTab === "tickets" && <SupportQueue />}
    </div>
  );
}
